import React from 'react'
import { useAtom } from 'jotai'

import {
    useAdminPracticeBase,
} from '@docpace/admin-react-hooks'
import {
    usePracticeTemplatesTranslations,
} from '@docpace/shared-react-hooks/usePracticeTemplatesTranslations'
import {
    useProviderDepartmentOptions,
} from '@docpace/shared-react-hooks/useProviderDepartmentOptions'
import {
    ctxPdoItem,
    ctxSelectedPdoIds,
} from '@docpace/shared-react-atoms'
import {
    ProviderDepartmentOptionDataType,
} from '@docpace/shared-ts-types'

import { AdminUpdateItemTranslationForm } from '@docpace/admin-react-forms/AdminUpdateItemTranslationForm'
import { AdminUpdateItemTristateForm } from '@docpace/admin-react-forms/AdminUpdateItemTristateForm'
import { AdminUpdateItemPracticeSlugForm } from '@docpace/admin-react-forms/AdminUpdateItemPracticeSlugForm'
import { AdminUpdateItemIntNumberForm } from '@docpace/admin-react-forms/AdminUpdateItemIntNumberForm'
import { AdminUpdateItemStatsTypeForm } from '@docpace/admin-react-forms/AdminUpdateItemStatsTypeForm'
import { AdminUpdateItemFileForm } from '@docpace/admin-react-forms/AdminUpdateItemFileForm'
import { AdminSearchGooglePlaceForm } from '@docpace/admin-react-forms/AdminSearchGooglePlaceForm'
import { useSnackbar } from 'notistack'
import { PdoConfigItem } from '@docpace/admin-react-components'
import { makeAdminProviderDepartmentOptionRows } from '../AdminProviderDepartmentOptionRows'
import Link from 'next/link'
import adminRoutes from '@docpace/shared-react-routes/adminRoutes'
import { useAdminGooglePlaceByIdLazyQuery, useAdminPracticeFilesQuery } from '@docpace/admin-react-apollo'

interface AdminProviderDepartmentOptionFormProps {
    googleMapsApiKey: string
}

export const AdminProviderDepartmentOptionForm = (
    props: AdminProviderDepartmentOptionFormProps
) => {
    const { googleMapsApiKey } = props
    const { enqueueSnackbar } = useSnackbar()
    const { practice, isLoading, providers } = useAdminPracticeBase()
    const { allTranslations } = usePracticeTemplatesTranslations()
    const [ configKey ] = useAtom(ctxPdoItem)
    const { data: practiceFilesData, refetch } = useAdminPracticeFilesQuery({
        variables: { practiceId: practice?.practiceId },
        skip: !practice?.practiceId
    })
    const [[ configIds, ...restIds ], setSelectedPdoIds] = useAtom(ctxSelectedPdoIds)
    const {
        getLatestOption,
        setOption,
    } = useProviderDepartmentOptions()

    const configSelectedPdo = getLatestOption({ ...configIds })

    const [ getAdminGooglePlaceById ] = useAdminGooglePlaceByIdLazyQuery()
    
    const pdoConfigItems: PdoConfigItem[] = makeAdminProviderDepartmentOptionRows({})
    const pdoConfigItem = pdoConfigItems?.find((r)=>{ return r.name === configKey })
    const [ __configItem, setPdoItem ] = useAtom(ctxPdoItem)
    // Changes are queued on the shared PDO queue - the page-level Submit
    // button upserts them in one batch, Cancel discards them
    async function handlePatchSubmit(formValues) {
        const rawValue = formValues?.[configKey]
        const value =
            rawValue === '' || rawValue === undefined ? null : rawValue
        try {
            setOption(configIds, configKey, value)
            if (configKey === 'googlePlaceId' && value) {
                await getAdminGooglePlaceById({ variables: { placeId: value } })
            }
            enqueueSnackbar('Queued - press Submit to save', {
                variant: 'success',
            })
            setSelectedPdoIds([])
            setPdoItem(null)
        } catch (e) {
            enqueueSnackbar(
                'there was a problem - check the console for errors',
                {
                    variant: 'error',
                }
            )
            console.log(e)
        }
    }

    const dialogFormDefaultValues = {
        title: configKey,
        name: configKey,
        defaultValues: {
            [configKey]:
                configSelectedPdo?.[configKey],
        },
        onCancel: () => {
            setPdoItem(null)
            setSelectedPdoIds([])
        },
        onSubmit: handlePatchSubmit,
    }

    return (<div className="p-2 bg-white w-full" style={{ maxWidth: 600 }}>
        <div 
            className="border-solid bg-gray-200 rounded text-sm mb-2 whitespace-normal w-full" 
        >
            {JSON.stringify(configIds ?? {})}
        </div>
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.googlePlace && googleMapsApiKey && (
            <Link href={{
                pathname: adminRoutes.PracticeSetGooglePlace.hrefNext({ practiceId: configIds.practiceId }),
                query: { ...configIds, practiceId: undefined }
            }}>
                <button className='btn btn-primary btn-sm'>
                    Search
                </button>
            </Link>
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.translation && (
            <AdminUpdateItemTranslationForm
                {...dialogFormDefaultValues}
                translationType={pdoConfigItem?.translationType}
                translations={allTranslations}
            />
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.checkbox && (
            <AdminUpdateItemTristateForm {...dialogFormDefaultValues}/>
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.intNumber && (
            <AdminUpdateItemIntNumberForm {...dialogFormDefaultValues}/>
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.practiceSlug && (
            <AdminUpdateItemPracticeSlugForm
                {...dialogFormDefaultValues}
                practiceSlugs={practice?.practiceSlugs?.nodes}
            />
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.statsType && (
            <AdminUpdateItemStatsTypeForm {...dialogFormDefaultValues} />
        )}
        {pdoConfigItem?.type ===
            ProviderDepartmentOptionDataType.file && (
            <AdminUpdateItemFileForm {...dialogFormDefaultValues} practiceFiles={practiceFilesData?.practice?.practiceFiles?.nodes} />
        )}
    </div>)
}
